import React, { useEffect, useRef, useState } from "react"
import {
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  XCircle,
} from "lucide-react"
import type { QuestionAssessmentRecord } from "../types"
import {
  formatAssessorDate,
  formatAssessorTime,
  pctOf,
} from "../utils/format"
import { feedbackErrorOf, feedbackPhaseOf } from "../utils/feedbackPhase"
import ConfidenceBadge from "./ConfidenceBadge"
import FeedbackPhaseChip from "./FeedbackPhaseChip"
import QuestionFeedback from "./QuestionFeedback"
import ScoreRing from "./ScoreRing"
import "./AssessmentCard.style.scss"

interface QuestionAssessmentCardProps {
  record: QuestionAssessmentRecord
  startExpanded?: boolean
}

const QuestionAssessmentCard = ({
  record,
  startExpanded = false,
}: QuestionAssessmentCardProps) => {
  const [expanded, setExpanded] = useState(startExpanded)
  const articleRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!startExpanded) return
    setExpanded(true)
    articleRef.current?.scrollIntoView({ block: "center" })
  }, [startExpanded])
  const { attempt } = record
  const phase = feedbackPhaseOf(record)
  const ready = phase === "ready"
  const score = attempt.score ?? 0
  const max = attempt.maxScore ?? 1
  const pct = pctOf(score, max)

  return (
    <article className="assessment-card" ref={articleRef}>
      <header className="assessment-card__header">
        <div className="assessment-card__chips">
          <span className="assessment-card__chip is-question">
            <BookOpen size={12} /> Question
          </span>
          <span className="assessment-card__chip">{record.subjectTitle}</span>
          <span className="assessment-card__chip">
            {record.year != null ? `${record.year} · ` : ""}
            Attempt #{attempt.attemptNumber}
          </span>
          <FeedbackPhaseChip phase={phase} />
          {ready && <ConfidenceBadge confidence={attempt.confidence} />}
        </div>
        <span className="assessment-card__date">
          {formatAssessorDate(record.date)}
        </span>
      </header>

      <div className="assessment-card__body">
        {!ready ? (
          <>
            <p className="assessment-card__snippet">{record.snippet}</p>
            <p className="assessment-card__notice">
              {phase === "pending"
                ? "AI is marking this attempt. Feedback will show here when it’s ready."
                : feedbackErrorOf(record) ||
                  "AI marking could not be completed for this attempt."}
            </p>
          </>
        ) : (
          <>
            <div className="assessment-card__summary">
              <ScoreRing pct={pct} />
              <div className="assessment-card__copy">
                <p className="assessment-card__snippet">{record.snippet}</p>
                <div className="assessment-card__meta">
                  <strong>
                    {score}/{max} marks
                  </strong>
                  <span>·</span>
                  <span>
                    <Clock size={12} /> {formatAssessorTime(attempt.timeSpentSeconds)}
                  </span>
                  {record.topicPath && (
                    <>
                      <span>·</span>
                      <span>{record.topicPath}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {attempt.criteriaBreakdown && attempt.criteriaBreakdown.length > 0 && (
              <div className="assessment-card__criteria">
                {attempt.criteriaBreakdown.map((c) => (
                  <span
                    key={c.label}
                    className={`assessment-card__criterion ${
                      c.marksAwarded > 0 ? "is-pass" : "is-fail"
                    }`}
                  >
                    {c.marksAwarded > 0 ? (
                      <CheckCircle2 size={12} />
                    ) : (
                      <XCircle size={12} />
                    )}
                    {c.label}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {ready && (
        <>
          <button
            type="button"
            className="assessment-card__toggle"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? (
              <>
                <ChevronUp size={14} /> Hide Full Feedback
              </>
            ) : (
              <>
                <ChevronDown size={14} /> View Full Feedback
              </>
            )}
          </button>

          {expanded && (
            <QuestionFeedback
              criteria={attempt.criteriaBreakdown}
              strengths={attempt.strengths}
              improvements={attempt.improvements}
              suggestedAnswer={attempt.suggestedAnswer}
              examinerFeedback={attempt.examinerFeedback}
            />
          )}
        </>
      )}
    </article>
  )
}

export default QuestionAssessmentCard
